import { QueryClient, QueryFunction } from "@tanstack/react-query";

// API Base URL - HARD-CODED to Railway backend (no more URL confusion)
const API_BASE_URL = 'https://main-veritus-api-production.up.railway.app';

async function defaultQueryFn({ queryKey }: { queryKey: any }) {
  const [url, unauthorizedBehavior] = queryKey;
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Get token from localStorage for authenticated requests
  const token = localStorage.getItem('acroniq_token');
  console.log('🔒 STABILITY: Query request:', { hasToken: !!token, url, fullUrl });
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    console.log('✅ STABILITY: Added auth header to request');
  } else {
    console.log('❌ STABILITY: No token found in localStorage for request');
  }
  
  const res = await fetch(fullUrl, {
    credentials: 'include',
    headers,
  });

  console.log('🔒 STABILITY: Response status:', res.status, 'for', fullUrl);

  async function throwIfResNotOk(res: Response) {
    if (!res.ok) {
      const text = (await res.text()) || res.statusText;
      console.log('❌ STABILITY: Request failed:', res.status, text);
      throw new Error(`${res.status}: ${text}`);
    }
  }

  if (unauthorizedBehavior === "returnNull" && res.status === 401) {
    console.log('⚠️ STABILITY: 401 returned as null');
    return null;
  }

  await throwIfResNotOk(res);
  return res.json();
}

export async function apiRequest<T>(
  method: string, 
  url: string, 
  data?: any, 
  options?: { headers?: Record<string, string> }
): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Get token from localStorage for authenticated requests
  const token = localStorage.getItem('acroniq_token');
  console.log('🔒 STABILITY: API request:', { method, url, fullUrl, hasToken: !!token });
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers || {}),
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    console.log('✅ STABILITY: Added auth header to API request');
  } else {
    console.log('❌ STABILITY: No token found in localStorage for API request');
  }
  
  const res = await fetch(fullUrl, {
    method,
    credentials: 'include',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  console.log('🔒 STABILITY: API response status:', res.status, 'for', fullUrl);

  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    console.log('❌ STABILITY: API request failed:', res.status, text);
    throw new Error(`${res.status}: ${text}`);
  }

  const json = await res.json();
  return json as T;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn as QueryFunction,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
